import { Slot, Slottable } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * Variants map onto *intent*, not appearance, so that a chamber's theme can repaint
 * them wholesale:
 *
 *   primary    the one action this screen exists for. Brand ground, at most one visible.
 *   accent     a highlight CTA on a light surface — the lime treatment from spec §16.
 *   secondary  everything else that is a real action.
 *   ghost      navigation and low-weight actions; no border until hovered.
 *   danger     destructive and irreversible. Revoke, Reject, Delete.
 *   link       inline in prose.
 */
type Variant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger' | 'link';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-[var(--pv-brand)] text-[var(--pv-brand-on)] border-[var(--pv-brand)] hover:bg-[var(--pv-brand-hover)] hover:border-[var(--pv-brand-hover)] shadow-[var(--pv-shadow-xs)]',
  accent:
    'bg-[var(--pv-accent)] text-[var(--pv-accent-on)] border-[var(--pv-accent)] hover:brightness-[0.97] font-semibold',
  secondary:
    'bg-[var(--pv-card)] text-[var(--pv-ink)] border-[var(--pv-line-strong)] hover:bg-white hover:border-[var(--pv-muted)]',
  ghost:
    'bg-transparent text-[var(--pv-ink-soft)] border-transparent hover:bg-[rgba(20,48,30,0.06)] hover:text-[var(--pv-ink)]',
  danger:
    'bg-[var(--pv-stop)] text-white border-[var(--pv-stop)] hover:brightness-95 focus-visible:outline-[var(--pv-stop)]',
  link: 'bg-transparent border-transparent text-[var(--pv-leaf-deep)] underline underline-offset-[3px] decoration-[var(--pv-leaf)] hover:decoration-[var(--pv-leaf-deep)] px-0 h-auto',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 [font-size:var(--pv-text-xs)] gap-1.5 rounded-[var(--pv-radius-sm)]',
  md: 'h-9 px-3.5 [font-size:var(--pv-text-sm)] gap-2 rounded-[var(--pv-radius)]',
  lg: 'h-11 px-5 [font-size:var(--pv-text-body)] gap-2 rounded-[var(--pv-radius)]',
  icon: 'h-9 w-9 p-0 justify-center rounded-[var(--pv-radius)]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /**
   * Renders onto the child element instead of a <button> — for a react-router <Link>
   * that should look like a button.
   *
   * The children are wrapped in <Slottable> so that the icons below can still be
   * injected as siblings. Without it, Radix's Slot sees three children (icon, child,
   * icon) and throws "Slot failed to slot onto its children", which is exactly what
   * happened the first time this rendered.
   */
  asChild?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    asChild = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      // A loading button stays focusable but announces itself as busy; disabling it
      // moves focus to the document and loses the user's place mid-task.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap border font-medium',
        'transition-[background-color,border-color,color,box-shadow] duration-[var(--pv-duration-fast)] ease-[var(--pv-ease)]',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : leadingIcon}
      <Slottable>{children}</Slottable>
      {!loading && trailingIcon}
    </Comp>
  );
});
